import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const BRAZIL_SCHEDULE_TIMEZONE = 'America/Sao_Paulo';

const DAY_MONTH_YEAR_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const HOUR_MINUTE_REGEX = /^(\d{2}):(\d{2})$/;

export function isScheduleTimezoneEnabled() {
  const env = String(import.meta.env.VITE_ENV || '').trim().toLowerCase();
  return env === 'homolog' || env === 'prod';
}

export function resolveScheduleTimezone() {
  return isScheduleTimezoneEnabled() ? BRAZIL_SCHEDULE_TIMEZONE : null;
}

/** @deprecated Use resolveScheduleTimezone() for runtime resolution. */
export const SCHEDULE_TIMEZONE = resolveScheduleTimezone() || 'utc';

function resolveTimezone(tz) {
  if (tz) {
    return tz;
  }

  return resolveScheduleTimezone();
}

export function parseScheduleFieldsToDate(
  publishDate = '',
  publishTime = '12:00',
  tz,
) {
  const normalizedDate = String(publishDate || '').trim();
  const normalizedTime = String(publishTime || '12:00').trim();

  const dateMatch = normalizedDate.match(DAY_MONTH_YEAR_REGEX);
  const timeMatch = normalizedTime.match(HOUR_MINUTE_REGEX);
  if (!dateMatch || !timeMatch) {
    return null;
  }

  const day = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const year = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);

  const scheduleTimezone = resolveTimezone(tz);
  if (scheduleTimezone) {
    const isoLocal = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const parsed = dayjs.tz(isoLocal, 'YYYY-MM-DD HH:mm', scheduleTimezone);

    if (!parsed.isValid()) {
      return null;
    }

    if (
      parsed.year() !== year ||
      parsed.month() + 1 !== month ||
      parsed.date() !== day
    ) {
      return null;
    }

    return parsed.toDate();
  }

  const parsed = new Date(year, month - 1, day, hour, minute);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() + 1 !== month ||
    parsed.getDate() !== day ||
    parsed.getHours() !== hour ||
    parsed.getMinutes() !== minute
  ) {
    return null;
  }

  return parsed;
}

export function formatDateToScheduleFields(
  value,
  tz,
) {
  if (!value) {
    return { publishDate: '', publishTime: '' };
  }

  const scheduleTimezone = resolveTimezone(tz);
  const parsed = scheduleTimezone ? dayjs(value).tz(scheduleTimezone) : dayjs(value);
  if (!parsed.isValid()) {
    return { publishDate: '', publishTime: '' };
  }

  return {
    publishDate: parsed.format('DD/MM/YYYY'),
    publishTime: parsed.format('HH:mm'),
  };
}

export function parseScheduleFieldsToIso(
  publishDate = '',
  publishTime = '12:00',
  tz,
) {
  const parsed = parseScheduleFieldsToDate(publishDate, publishTime, tz);
  return parsed ? parsed.toISOString() : null;
}

export function formatPublicationTimeInScheduleTimezone(
  value,
  tz,
) {
  if (!value) {
    return '';
  }

  const scheduleTimezone = resolveTimezone(tz);
  const parsed = scheduleTimezone ? dayjs(value).tz(scheduleTimezone) : dayjs(value);
  if (!parsed.isValid()) {
    return '';
  }

  return parsed.format('HH:mm');
}
